import {Component, ViewChild} from '@angular/core';
import {AppService} from '../app.service';
import {ContentType, TableColumn} from 'src/model/page';
import {CaseSearchRequirementDto, JsonCase, JsonCaseTongue, ItemDto, JsonMember, MemberRole, CountDto, SimpleCaseDto, TaggerType} from 'src/model/dto';
import {DateUtility} from '../utility/DateUtility';
import {Utility} from '../utility/Utility';
import {CaseDialogComponent} from './case-dialog/case-dialog.component';
import {MessageService, SelectItem} from 'primeng/api';
import {CaseTaggingComponent} from '../component/case-tagging/case-tagging.component';
import {CaseCompareComponent} from "./case-compare/case-compare.component";
import {CaseEditDialogComponent} from "./case-edit-dialog/case-edit-dialog.component";

@Component({
  selector: 'app-case-list',
  templateUrl: './case-list.component.html',
  styleUrls: ['./case-list.component.scss']
})
export class CaseListComponent {
  @ViewChild(CaseDialogComponent) caseDialog: CaseDialogComponent;
  @ViewChild(CaseEditDialogComponent) caseEditDialog: CaseEditDialogComponent;
  @ViewChild(CaseTaggingComponent) taggingComponent: CaseTaggingComponent;
  @ViewChild(CaseCompareComponent) caseCompareDialog: CaseCompareComponent;
  tagOptions: SelectItem[] = [
    {label: 'Untagged Cases', value: 0},
    {label: 'Tagged Cases', value: 1},
    {label: 'All Cases', value: 2}
  ]
  memberOptions: SelectItem[] = [];
  cols: TableColumn[];
  fromDate: Date;
  toDate: Date;
  onlyMe: boolean;
  name = '';
  tag: SelectItem; // 0: untagged, 1: tagged, 2: all
  tagger?: SelectItem;
  caseIDStr = '';
  cases: SimpleCaseDto[] = [];
  mode = 'list'; // list | tagging
  selectedCase: JsonCase;
  compareCases: JsonCase[] = [];
  isShowCompareButton = false;
  first = 0;
  pageSize = 10;
  pageIndex = 0;
  totalCount = 0;
  Utility = Utility;

  constructor(
    private appService: AppService,
    private msgService: MessageService
  ) {}

  ngOnInit(): void {
    this.toDate = new Date();
    this.fromDate = DateUtility.addDays(this.toDate, -365 * 5);
    this.onlyMe = !this.isAdmin;
    this.tag = this.tagOptions[2];
    this.initCols();
    this.initialMemberOptions()
    this.loadData();
  }

  initialMemberOptions(): void {
    if (this.isAdmin) {
      this.appService.get('data', 'getTaggers', {}).subscribe((res: ItemDto[]) => {
        this.memberOptions = [{ label: 'No Constraints', value: 0 }];
        res.forEach(item => {
            this.memberOptions.push({
              label: item.name,
              value: item.id
            });
        });
        this.tagger = this.memberOptions[0];
      });
    }
  }

  initCols(): void {
    this.cols = [
      {header: 'ID', field: 'id'},
      {header: 'Name', field: 'name'},
      {header: 'Doctor', field: 'doctorName'},
      {header: 'Tags', field: 'tongues'},
      {header: 'Time', field: 'createTime'}
    ];
  }

  get isAdmin(): boolean {
    return this.appService.loginInfo.role === MemberRole.admin || 
      this.appService.loginInfo.role === MemberRole.tagger;
  }

  loadData(resetPage: boolean = true): void {
    if (resetPage) {
      this.first = 0;
      this.pageIndex = 0;
      this.fetchTotalCount();
    }
    const taggerId = this.tagger? this.tagger.value: 0;
    const data: CaseSearchRequirementDto = {
      fromDate: DateUtility.toDateString(this.fromDate),
      toDate: DateUtility.toDateString(this.toDate),
      onlyMe: this.onlyMe? 1: 0,
      name: this.name,
      caseId: Number(this.caseIDStr)? Number(this.caseIDStr): 0,
      tag: this.tag.value,
      taggerId: taggerId === 0? null: taggerId,
      pageSize: this.pageSize,
      pageNumber: this.pageIndex
    };
    this.isShowCompareButton = false;
    this.appService.postBody('data', 'searchSimpleCases', JSON.stringify(data), ContentType.json).subscribe((res: SimpleCaseDto[]) => {
      this.cases = res;
      if (this.name && this.name.length > 0 && this.cases.length > 1) {
        this.isShowCompareButton = true;
      }
    });
  }

  fetchTotalCount(): void {
    const taggerId = this.tagger? this.tagger.value: 0;
    const data: CaseSearchRequirementDto = {
      fromDate: DateUtility.toDateString(this.fromDate),
      toDate: DateUtility.toDateString(this.toDate),
      onlyMe: this.onlyMe? 1: 0,
      name: this.name,
      caseId: Number(this.caseIDStr)? Number(this.caseIDStr): 0,
      tag: this.tag.value,
      taggerId: taggerId === 0 ? null : taggerId,
      pageSize: this.pageSize,
      pageNumber: this.pageIndex
    };
    this.appService.postBody('data', 'countSimpleCases', JSON.stringify(data), ContentType.json).subscribe((res: CountDto) => {
      this.totalCount = res.count;
    });
  }

  onPageChange(event: any): void {
    this.pageSize = event.rows;
    this.pageIndex = event.first / event.rows;
    this.first = event.first;
    this.loadData(false);
  }

  onSelect(selectedCase: SimpleCaseDto): void {
    this.appService.setBlock(true);
    this.appService.get('data', 'getCase', {caseId: selectedCase.id}).subscribe((res: JsonCase) => {
      this.selectedCase = res;
      setTimeout(() => {
        this.caseDialog.setShow(true)
        this.appService.setBlock(false);
      }, 100);
    });
  }

  onEdit(updatedCase: JsonCase): void {
    this.caseEditDialog.setShow(true);
  }

  onStartTagging(updatedCase: JsonCase): void {
    setTimeout(() => {
      this.mode = 'tagging';
      setTimeout(() => {
        this.taggingComponent.reset();
      }, 100);
    }, 100);
  }

  onDelete(caseId: number) : void {
    const index = this.cases.findIndex(c => c.id === caseId);
    if (index !== -1) {
      this.cases.splice(index, 1);
    }
  }

  onUpdateTags(newCase: JsonCase): void {
    const editingIndex = this.cases.findIndex(c => c.id === newCase.id);
    if (editingIndex !== -1) {
      this.cases[editingIndex].tongues = newCase.tongues;
      this.selectedCase = newCase;
      this.mode = 'list';
      setTimeout(() => {
        this.caseDialog.setShow(true);
      }, 100);
    }
    this.loadData(false);
  }

  onUpdateTagsAndNext(newCase: JsonCase): void {
    const editingIndex = this.cases.findIndex(c => c.id === newCase.id);
    if (editingIndex === -1) {
      this.msgService.add({severity: 'error', summary: 'Error', detail: 'Fail to find the case to update.'});
      return;
    }
    const nextIndex = editingIndex + 1;
    if (nextIndex < this.cases.length) {
      this.appService.setBlock(true);
      this.appService.get('data', 'getCase', {caseId: this.cases[nextIndex].id}).subscribe((res: JsonCase) => {
        this.selectedCase = res;
        setTimeout(() => {
          this.onStartTagging(this.selectedCase);
          this.appService.setBlock(false);
        }, 100);
      });
    } else {
      this.msgService.add({severity: 'info', summary: 'Info', detail: 'No more case to tag.'});
      this.onUpdateTags(newCase);
    }
  }

  onCancelTags(): void {
    this.mode = 'list';
    setTimeout(() => {
      this.caseDialog.setShow(true);
    }, 100);
  }

  onSaveEdit(patientCase: JsonCase): void {
    this.selectedCase = patientCase;
    setTimeout(() => {
      this.caseDialog.setShow(true);
    }, 100);
  }

  onCancelEdit(patientCase: JsonCase): void {
    setTimeout(() => {
      this.caseDialog.setShow(true);
    }, 100);
  }

  getFilteredTongues(tongues: JsonCaseTongue[]): JsonCaseTongue[] {
    return tongues.filter(tongue => tongue.tagger.id > 0);
  }

  getTagStr(tongues: JsonCaseTongue[]): string {
    return this.getFilteredTongues(tongues)
      .map(tongue => Utility.toTongueStr(tongue)).join(', ');
  }

  onCompare(): void {
    this.appService.setBlock(true);
    let caseIds = this.cases.map(c => c.id);
    this.appService.postBody('data', 'getCases', JSON.stringify(caseIds), ContentType.json).subscribe((res: JsonCase[]) => {
      this.compareCases = res.sort((a, b) => a.createTime > b.createTime? 1: -1);
      this.caseCompareDialog.show();
      this.appService.setBlock(false);
    });
  }

  getTaggerStrs(patientCase: JsonCase): string[] {
    let result = [];
    if (patientCase.createTagger) {
      result.push('The first tagger: ' + patientCase.createTagger.name);
    }
    if (patientCase.lastTagger) {
      result.push('The last tagger: ' + patientCase.lastTagger.name);
    }
    return result;
  }
}
